import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Clock, Calendar, Award, CheckCircle, XCircle, BarChart } from 'lucide-react';
//@ts-ignore  
import image from '../assets/pngs/java-png.png';
import { useToast } from '../../hooks/use-toast';
import { getAuth } from "firebase/auth";
import { collection, doc, getDoc, getDocs, getFirestore, query, where } from "firebase/firestore";
import { app } from "../../firebase";
import { useLoading } from '../../hooks/useLoading';
import { toast } from 'react-toastify';

export const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const db = getFirestore(app);
  const auth = getAuth(app);
  const [courses, setCourses] = useState([]);
  const { setIsLoading } = useLoading();
  const [studentData, setStudentData] = useState(null); // State for student details  

  useEffect(() => {
    const checkUserLoggedIn = async () => {
      setIsLoading(true);
      const userData = JSON.parse(localStorage?.getItem('user')!) ?? null;
      if (userData && userData.accessToken) {
        try {
          const studentQuery = query(
            collection(db, "students"),
            where("emailId", "==", userData.emailId)
          );
          const studentSnapshot = await getDocs(studentQuery);

          if (!studentSnapshot.empty) {
            const studentDoc = studentSnapshot.docs[0];
            const studentDetails: any = { id: studentDoc.id, ...studentDoc.data() };
            setStudentData(studentDetails);
            checkLMSAccess(studentDetails);

            // Retrieve courses related to the student  
            const courses = studentDetails?.courses || []; // Assuming courses is an array of course IDs  
            const coursesCollection = collection(db, "courses");
            const coursesList = await Promise.all(courses.map(async (c) => {
              const courseDocRef = doc(coursesCollection, c.courseId);
              const courseDoc = await getDoc(courseDocRef);
              return { id: courseDoc.id, ...courseDoc.data() };
            }));

            setCourses(coursesList);
            setIsLoading(false);
            setIsLoading(false);
          } else {
            console.error("No student found with this emailId.");
            localStorage.clear();
            navigate("/login");
          }
        } catch (error) {
          console.error("Error during fetching student or courses:", error);
          navigate("/login");
        }
      } else {
        navigate("/login");
      }
    };

    checkUserLoggedIn();
  }, [setIsLoading, navigate]);

  // Mock data for attendance  
  const attendance = [
    { course: 'Automation Testing', conducted: 2, attended: 0, absent: 2, percentage: 0 },
    { course: 'Aptitude', conducted: 2, attended: 0, absent: 2, percentage: 0 },
    { course: 'ReactJS', conducted: 17, attended: 0, absent: 17, percentage: 0 },
  ];


  const checkLMSAccess = (student) => {
    let access = student?.isLMSDone == true;
    if (!access) {
      toast({
        title: "Access Denied",
        description: "You Access Has Been Revoked , Please contact Reception",
        variant: "destructive",
      });
      localStorage.clear();
      window.location.href = "/";
    }
  }
  // Mock data for notifications  
  const notifications = [];

  return (
    <div className="space-y-6 animate-fade-in p-4 md:p-6 w-full overflow-hidden">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 w-full">
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-bytesplash-primary tracking-tight">Dashboard</h1>
          <p className="text-bytesplash-gray-500">Welcome back! Here's an overview of your learning journey.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Student ID:</span>
          <span className="bg-bytesplash-light text-bytesplash-primary px-3 py-1 rounded-md font-bold">{studentData?.studentIdNo}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="shadow-soft w-full">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen size={18} className="text-bytesplash-primary" />
              Enrolled Courses
            </CardTitle>
            <CardDescription>Your current courses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {courses?.map((course, i) => (
                <Link key={course.id + i + 1} to={`/courses/${course.id}`} className="block group">
                  <div className="flex gap-4 items-center p-3 rounded-lg hover:bg-bytesplash-light/50 transition-all duration-200">
                    <div className="w-12 h-12 rounded-md bg-bytesplash-light flex items-center justify-center overflow-hidden">
                      <img src={course?.thumbnail?.url} alt={course.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium group-hover:text-bytesplash-primary transition-colors">{course?.title ?? course?.courseName}</h4>
                      <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center gap-1">
                          <span className="course-tag bg-bytesplash-light text-bytesplash-primary">{course?.level}</span>
                        </div>
                        <span className="text-xs text-bytesplash-gray-500">{course.progress}% completed</span>
                      </div>
                      <Progress value={course.progress} className="h-1 mt-2" />
                    </div>
                  </div>
                </Link>
              ))}
              <Link to="/courses" className="flex items-center justify-center gap-2 text-bytesplash-primary hover:text-bytesplash-dark mt-2 py-2 text-sm font-medium">
                View all courses <ArrowRight size={16} />
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-soft w-full">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock size={18} className="text-bytesplash-primary" />
              Upcoming Sessions
            </CardTitle>
            <CardDescription>Your scheduled classes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {['Java Fundamentals', 'Interview Preparation', 'Python Workshop'].map((session, index) => (
                <div key={index} className="p-3 border border-bytesplash-gray-200 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium">{session}</h4>
                      <p className="text-sm text-bytesplash-gray-500">Course details</p>
                    </div>
                    <span className="badge bg-bytesplash-light text-bytesplash-primary">Date</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-sm text-bytesplash-gray-600">
                    <Calendar size={14} />
                    <span>Time</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-soft w-full">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Award size={18} className="text-bytesplash-primary" />
              Achievements
            </CardTitle>
            <CardDescription>Your learning milestones</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3 items-center justify-center h-full py-4">
              <div className="w-20 h-20 rounded-full bg-bytesplash-light flex items-center justify-center">
                <Award size={36} className="text-bytesplash-primary" />
              </div>
              <h3 className="font-semibold text-lg mt-2">Just Getting Started</h3>
              <p className="text-center text-bytesplash-gray-500 text-sm">
                Complete your first course to unlock achievements and track your progress.
              </p>
              <Link to="/courses" className="mt-4 bg-bytesplash-primary text-white px-4 py-2 rounded-md hover:bg-bytesplash-dark transition-colors duration-200">
                Explore Courses
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="notifications" className="space-y-4">
        <TabsList>
          <TabsTrigger value="attendance">Attendance Details</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>
        <TabsContent value="attendance" className="space-y-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Student Attendance Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="verflow-x-auto max-w-[90vw]">
                <table className="border-collapse text-sm overflow-x-auto max-w-[90vw]">
                  <thead>
                    <tr>
                      <th className="table-header">
                        <BookOpen size={18} className="mr-0" />
                      </th>
                      <th className="table-header">
                        <Calendar size={18} className="mr-0" />
                      </th>
                      <th className="table-header">
                        <CheckCircle size={18} className="mr-0" />
                      </th>
                      <th className="table-header">
                        <XCircle size={18} className="mr-0" />
                      </th>
                      <th className="table-header">
                        <BarChart size={18} className="mr-0" /> %
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendance.map((item, index) => (
                      <tr key={index} className="border-b border-bytesplash-gray-200 hover:bg-bytesplash-gray-50">
                        <td className="table-cell">{item.course}</td>
                        <td className="table-cell text-center">{item.conducted}</td>
                        <td className="table-cell text-center">{item.attended}</td>
                        <td className="table-cell text-center">{item.absent}</td>
                        <td className="table-cell text-center">{item.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">New Notifications</CardTitle>
            </CardHeader>
            <CardContent>
              {notifications.length > 0 ? (
                <div className="space-y-3">
                  {notifications.map((notification, index) => (
                    <div key={index} className="p-3 border border-bytesplash-gray-200 rounded-lg">
                      {/* Notification content */}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <p className="text-bytesplash-gray-500">No new notifications</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};  
